import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import CloudOffRoundedIcon from "@mui/icons-material/CloudOffRounded";
import ConstructionRoundedIcon from "@mui/icons-material/ConstructionRounded";
import HorizontalRuleRoundedIcon from "@mui/icons-material/HorizontalRuleRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import RedeemRoundedIcon from "@mui/icons-material/RedeemRounded";

import { useApi } from "../../../hooks/useApi";
import useResource from "../components/useResource";
import {
  DOMAINS,
  CHECKS,
  ADDONS,
  classificationToTier,
  evaluateCheck,
  groupBySubcategory,
  summarizeDomain,
} from "../health/healthRegistry";
import { buildOverrideMap, unmatchedDirectives } from "../health/directives";
import CheckDetailDrawer from "../health/CheckDetailDrawer";
import ComplianceSection from "../health/ComplianceSection";

const STATUS_META = {
  pass: { Icon: CheckCircleRoundedIcon, color: "success.main", label: "Pass" },
  fail: { Icon: CancelRoundedIcon, color: "error.main", label: "Fail" },
  warn: {
    Icon: WarningAmberRoundedIcon,
    color: "warning.main",
    label: "Warning",
  },
  manual: {
    Icon: RadioButtonUncheckedRoundedIcon,
    color: "text.disabled",
    label: "Not recorded",
  },
  nodata: {
    Icon: HourglassEmptyRoundedIcon,
    color: "text.disabled",
    label: "Audit pending",
  },
  excluded: {
    Icon: RemoveCircleOutlineRoundedIcon,
    color: "text.disabled",
    label: "Not in plan",
  },
  notapplicable: {
    Icon: HorizontalRuleRoundedIcon,
    color: "text.disabled",
    label: "Nothing to monitor",
  },
  notmanaged: {
    Icon: CloudOffRoundedIcon,
    color: "info.main",
    label: "Not managed by CMA (no Graph access)",
  },
  pending: {
    Icon: ConstructionRoundedIcon,
    color: "text.disabled",
    label: "Producer not live yet",
  },
};

function StatusIcon({ status }) {
  const meta = STATUS_META[status] || STATUS_META.nodata;
  const { Icon } = meta;
  return (
    <Tooltip title={meta.label}>
      <Icon sx={{ color: meta.color, fontSize: 20 }} />
    </Tooltip>
  );
}

/**
 * Technical Health tab — reads automated check results from the `audit`
 * container (cma/audit) plus Knowledge directives (accountprofile) and
 * renders six domain accordions. Gap-type chips are derived from the customer's
 * CMA365 tier (classification). Manual checks are editable; automated checks can
 * be overridden by a vCIO.
 */
export default function TechnicalHealthTab({
  code,
  classification,
  refreshKey,
}) {
  const theme = useTheme();
  const jApi = useApi();
  const [bump, setBump] = useState(0);
  const reloadKey = `${refreshKey}-${bump}`;
  const [selectedDate, setSelectedDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const audit = useResource(
    `cma/audit?account=${encodeURIComponent(code)}&auditDate=${selectedDate}`,
    { refreshKey: reloadKey },
  );
  const profile = useResource(
    `cma/accountprofile?account=${encodeURIComponent(code)}`,
    { refreshKey: reloadKey },
  );
  const compliance = useResource(
    `cma/complianceprofile?account=${encodeURIComponent(code)}`,
    { refreshKey: reloadKey },
  );

  const tier = classificationToTier(classification);

  // A dotted account code (asl.gln) is a sub-location sharing its parent's M365 tenant.
  // Fallback target for the "view on <parent>" link when a producer flagged a check
  // tenantWide but did not carry parentAccount through.
  const parentCode = code && code.includes('.') ? code.slice(0, code.lastIndexOf('.')) : null;

  // Group audit records by auditRunId. Per-customer "one-offs" now live as
  // Knowledge (accountprofile) entries carrying a structured `directive` —
  // manual values, vCIO overrides, add-on entitlements, and device suppressions.
  // We index them by check/addon and keep each entry's id for write-back.
  const {
    auditByRunId,
    manualByKey,
    overrideByKey,
    unknownDirectives,
    entitlements,
    entitlementId,
    suppressByCheck,
    suppressEntryId,
    circuitCapacityByCheck,
    circuitCapacityEntryId,
  } = useMemo(() => {
    const byRun = {};
    for (const r of Array.isArray(audit.data) ? audit.data : []) {
      const k = r.auditRunId || "UNKNOWN";
      (byRun[k] = byRun[k] || []).push(r);
    }
    // Audits write a fresh record per run (the AuditId embeds the date), so an
    // account accumulates one record per day. Keep only the LATEST record per
    // target within each run, so account-level audits resolve to a single record
    // (and per-device audits to the newest run per device). Target identity is the
    // record title (hostname for per-device audits, the account for account-level),
    // falling back to account/id.
    for (const k of Object.keys(byRun)) {
      const latest = new Map();
      for (const r of byRun[k]) {
        const key = r.title || r.account || r.id;
        const cur = latest.get(key);
        const d = String(r.auditDate || ""),
          u = String(r.lastUpdated || "");
        if (
          !cur ||
          d > String(cur.auditDate || "") ||
          (d === String(cur.auditDate || "") &&
            u > String(cur.lastUpdated || ""))
        ) {
          latest.set(key, r);
        }
      }
      byRun[k] = Array.from(latest.values());
    }

    const suppress = {};
    for (const k of Object.keys(byRun)) {
      for (const r of byRun[k]) {
        for (const ad of Array.isArray(r.appliedDirectives)
          ? r.appliedDirectives
          : []) {
          if (ad.kind === "suppress" && ad.checkKey && ad.target != null) {
            (suppress[ad.checkKey] = suppress[ad.checkKey] || new Set()).add(
              String(ad.target),
            );
          }
        }
      }
    }

    // Overrides are NOT read off appliedDirectives alone — see directives.js. The
    // stored accountprofile row stands on its own so an override on a check the
    // producers said nothing about still shows; the server echo wins where it exists.
    const profileRows = Array.isArray(profile.data) ? profile.data : [];
    const override = buildOverrideMap({
      profileRows,
      auditRecords: Object.values(byRun).flat(),
    });

    const manual = {},
      ents = new Set(),
      entId = {},
      supId = {},
      cap = {},
      capById = {};
    for (const p of profileRows) {
      const d = p.directive;
      if (!d || !d.kind) continue;
      if (d.kind === "manual" && d.check_key) {
        manual[d.check_key] = {
          id: p.id,
          value: d.value,
          value_type: d.value_type,
          notes: p.content,
          recorded_by: p.last_updated_by,
          lastUpdated: p.lastUpdated,
        };
      } else if (d.kind === "entitlement" && d.addon_key) {
        if (d.value === true || String(d.value).toLowerCase() === "true") {
          ents.add(d.addon_key);
          entId[d.addon_key] = p.id;
        }
      } else if (d.kind === "suppress" && d.check_key && d.target) {
        supId[`${d.check_key}::${d.target}`] = p.id;
      } else if (d.kind === "circuit_capacity" && d.check_key && d.target) {
        capById[`${d.check_key}::${d.target}`] = p.id;
        (cap[d.check_key] = cap[d.check_key] || new Map()).set(
          String(d.target),
          {
            upload_mbps: d.upload_mbps,
            download_mbps: d.download_mbps,
            provider: d.provider || "",
            link_type: d.link_type || "",
          },
        );
      }
    }
    return {
      auditByRunId: byRun,
      manualByKey: manual,
      overrideByKey: override,
      unknownDirectives: unmatchedDirectives(profileRows),
      entitlements: ents,
      entitlementId: entId,
      suppressByCheck: suppress,
      suppressEntryId: supId,
      circuitCapacityByCheck: cap,
      circuitCapacityEntryId: capById,
    };
  }, [audit.data, profile.data]);

  // Evaluate every check once.
  const evals = useMemo(() => {
    const map = {};
    for (const c of CHECKS) {
      map[c.key] = evaluateCheck(
        c,
        auditByRunId,
        manualByKey,
        overrideByKey,
        tier,
        entitlements,
        suppressByCheck,
      );
    }
    return map;
  }, [
    auditByRunId,
    manualByKey,
    overrideByKey,
    tier,
    entitlements,
    suppressByCheck,
  ]);

  const loading = audit.loading || profile.loading;

  // ── write helpers: every one-off is an accountprofile entry with a directive ──
  const writeDirective = async ({
    directive,
    topic,
    content,
    auditPaths,
    existingId,
  }) => {
    const body = {
      account: code,
      topic,
      content: content || `${directive.kind} directive`,
      scope: "always",
      tags: [directive.kind],
      directive,
    };
    if (existingId) {
      body.id = existingId;
      return jApi.request("PATCH", "cma/accountprofile", body);
    }
    return jApi.request("POST", "cma/accountprofile", body);
  };
  const deleteDirective = (id) =>
    jApi.request(
      "DELETE",
      `cma/accountprofile/${id}?account=${encodeURIComponent(code)}`,
    );

  // The per-target rows a check exposes (its audit details, or the record-level
  // target) — used to let a vCIO suppress a specific target from the dialog.
  const detailTargetsFor = (check) => {
    if (!check || check.source.type !== "audit") return [];
    const records = auditByRunId[check.source.auditRunId] || [];
    const ck = check.source.checkKey;
    const out = [];
    for (const r of records) {
      const c =
        ck && r.jobResults && r.jobResults.checks
          ? r.jobResults.checks[ck]
          : null;
      if (c && Array.isArray(c.details) && c.details.length)
        c.details.forEach((d) => out.push(String(d.target)));
      else out.push(String(r.title || r.account || r.id));
    }
    return [...new Set(out)];
  };

  // ── manual / override dialog state ──
  const [dialog, setDialog] = useState(null); // { mode:'manual'|'override', check }
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ── raw-data detail drawer ──
  const [detailCheck, setDetailCheck] = useState(null);

  // ── row-level Requeue: replay the fact's own VES job ──
  // Each producer fact stores the VES jobId that wrote it. We fetch that job and
  // re-POST its exact functionpath+splat, so the site/serial/device the original run
  // resolved comes along for free (and batch producers recompute the whole account).
  // Only today's facts are eligible — the replayed splat carries the fact's own
  // AuditDate, so a re-run never overwrites a historical day.
  const [requeueBusy, setRequeueBusy] = useState(false);
  const [requeueMsg, setRequeueMsg] = useState(null);
  const todayStr = new Date().toISOString().slice(0, 10);

  // Multiple producers can share one auditRunId (e.g. datto-server and datto-desktop both
  // write auditRunId "DATTO"), so selecting by auditRunID alone would replay whichever
  // OTHER producer's job happened to run today instead of the one that owns this checkKey.
  // Single source of truth: the Re-run button's enabled state and the replay itself MUST
  // use this same seletor, or the button offers a re-run that then finds nothing to do.
  const requeueJobIdsFor = (check) =>{
    if (!check || check.source.type !== "audit") return [];
    const ck = check.source.checkKey;
    if (!ck) return [];
    return [
      ... new Set(
        (auditByRunId[check.source.auditRunId] || []).filter(
          (r) => 
            r.auditDate === todayStr &&
            r.jobId &&
            r.jobResults &&
            r.jobResults.checks &&
            r.jobResults.checks[ck],
        ).map((r) => r.jobId),
      ),
    ];
  };

  const handleRequeue = async (check) => {
    if (!check || check.source.type !== "audit") return;
    const jobIds = requeueJobIdsFor(check);
  };

  // ── add-on entitlement dialog state ──
  const [addonOpen, setAddonOpen] = useState(false);
  const [addonForm, setAddonForm] = useState({}); // { [addonKey]: bool }
  const [addonSaving, setAddonSaving] = useState(false);
  const [addonError, setAddonError] = useState(null);

  const openAddons = () => {
    const init = {};
    for (const a of ADDONS) init[a.key] = entitlements.has(a.key);
    setAddonForm(init);
    setAddonError(null);
    setAddonOpen(true);
  };

  const saveAddons = async () => {
    try {
      setAddonSaving(true);
      setAddonError(null);
      // Diff the form against current entitlements: create a Knowledge entitlement
      // directive for newly-checked add-ons, delete the entry for unchecked ones.
      for (const a of ADDONS) {
        const want = !!addonForm[a.key];
        const have = entitlements.has(a.key);
        if (want === have) continue;
        if (want) {
          await writeDirective({
            directive: { kind: "entitlement", addon_key: a.key, value: true },
            topic: `Add-on: ${a.label}`,
            content: `Customer holds the '${a.label}' add-on.`,
          });
        } else if (entitlementId[a.key]) {
          await deleteDirective(entitlementId[a.key]);
        }
      }
      setAddonOpen(false);
      setBump((b) => b + 1);
    } catch (ex) {
      setAddonError(
        ex?.response?.data?.error?.message || ex?.toString?.() || "Save failed",
      );
    } finally {
      setAddonSaving(false);
    }
  };

  const openManual = (check) => {
    const rec = manualByKey[check.key];
    setForm({
      bool: rec
        ? rec.value === true ||
          String(rec.value).toLowerCase() === "true" ||
          String(rec.value).toLowerCase() === "yes"
        : false,
      date:
        rec && check.valueKind === "date" ? String(rec.value).slice(0, 10) : "",
      text: rec && check.valueKind === "text" ? String(rec.value) : "",
      notes: rec?.notes || "",
    });
    setError(null);
    setDialog({ mode: "manual", check, existing: !!manualByKey[check.key] });
  };

  const openOverride = (check) => {
    const o = overrideByKey[check.source.checkKey];
    setForm({
      value: o?.value || "PASS",
      reason: o?.reason || "",
      expires: o?.override_expires
        ? String(o.override_expires).slice(0, 10)
        : "",
      action: "override", // 'override' (whole check) | 'suppress' (a target)
      target: "",
    });
    setError(null);
    setDialog({
      mode: "override",
      check,
      // Only a STORED row can be removed; an echo-only entry has no id to delete.
      existing: !!o?.id,
      targets: detailTargetsFor(check),
    });
  };

  // Suppress a specific line item straight from the detail blade: open the same
  // override/suppress dialog the pencil uses, pre-set to 'suppress' for this target
  // (so the vCIO still records the required reason). The blade stays open behind it.
  const openSuppressForTarget = (check, target) => {
    if (!check) return;
    setForm({
      value: "PASS",
      reason: "",
      expires: "",
      action: "suppress",
      target: String(target),
    });
    setError(null);
    setDialog({
      mode: "override",
      check,
      existing: false,
      targets: detailTargetsFor(check),
    });
  };

  const closeDialog = () => {
    if (!saving) setDialog(null);
  };

  const removeSuppress = async (check, target) => {
    const id = suppressEntryId[`${check.source.checkKey}::${target}`];
    if (!id) return;
    try {
      setSaving(true);
      setError(null);
      await deleteDirective(id);
      setDialog(null);
      setBump((b) => b + 1);
    } catch (ex) {
      setError(
        ex?.response?.data?.error?.message ||
          ex?.toString?.() ||
          "Remove failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const openCircuitCapacity = (check, target) => {
    if (!check) return;
    const existing = circuitCapacityByCheck[check.source.checkKey]?.get(
      String(target),
    );
    setForm({
      action: "circuit_capacity",
      target: String(target),
      downloadMbps: existing?.download_mbps ?? "",
      uploadMbps: existing?.upload_mbps ?? "",
      provider: existing?.provider ?? "",
      linkType: existing?.link_type ?? "",
    });
    setError(null);
    setDialog({ mode: "circuit_capacity", check, existing: !!existing });
  };

  const removeCircuitCapacity = async (check, target) => {
    const id = circuitCapacityEntryId[`${check.source.checkKey}::${target}`];
    if (!id) return;
    try {
      setSaving(true);
      setError(null);
      await deleteDirective(id);
      setDialog(null);
      setBump((b) => b + 1);
    } catch (ex) {
      setError(
        ex?.response?.data?.error?.message ||
          ex?.toString?.() ||
          "Remove failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const clearValue = async () => {
    if (!dialog) return;
    const { mode, check } = dialog;
    if (mode === "circuit_capacity") {
      return removeCircuitCapacity(check, form.target);
    }
    const rec =
      mode === "override"
        ? overrideByKey[check.source.checkKey]
        : manualByKey[check.key];
    try {
      setSaving(true);
      setError(null);
      if (rec?.id) await deleteDirective(rec.id);
      setDialog(null);
      setBump((b) => b + 1);
    } catch (ex) {
      setError(
        ex?.response?.data?.error?.message ||
          ex?.toString?.() ||
          "Clear failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!dialog) return;
    const { mode, check } = dialog;
    try {
      setSaving(true);
      setError(null);
      if (mode === "circuit_capacity") {
        const up = Number(form.uploadMbps),
          dn = Number(form.downloadMbps);
        if (!(up > 0) || !(dn > 0)) {
          setError("Upload and download Mbps must be numbers greater than 0.");
          setSaving(false);
          return;
        }
        await writeDirective({
          directive: {
            kind: "circuit_capacity",
            check_key: check.source.checkKey,
            target: form.target,
            upload_mbps: up,
            download_mbps: dn,
            ...(form.provider?.trim()
              ? { provider: form.provider.trim() }
              : {}),
            ...(form.linkType?.trim()
              ? { link_type: form.linkType.trim() }
              : {}),
          },
          topic: `Circuit capacity: ${check.label} — ${form.target}`,
          content: `${up}/${dn} Mbps${form.provider?.trim() ? " · " + form.provider.trim() : ""}`,
          existingId:
            circuitCapacityEntryId[`${check.source.checkKey}::${form.target}`],
        });
      } else if (mode === "manual") {
        let value, value_type;
        if (check.valueKind === "boolean") {
          value = form.bool;
          value_type = "boolean";
        } else if (check.valueKind === "date") {
          value = form.date;
          value_type = "date";
        } else {
          value = form.text;
          value_type = "text";
        }
        await writeDirective({
          directive: {
            kind: "manual",
            check_key: check.key,
            value,
            value_type,
          },
          topic: `Manual: ${check.label}`,
          content: form.notes || `Recorded value: ${value}`,
          existingId: manualByKey[check.key]?.id,
        });
      } else if (form.action === "suppress") {
        if (!form.target) {
          setError("Choose a target to suppress.");
          setSaving(false);
          return;
        }
        if (!form.reason || !form.reason.trim()) {
          setError("A reason is required.");
          setSaving(false);
          return;
        }
        await writeDirective({
          directive: {
            kind: "suppress",
            check_key: check.source.checkKey,
            target: form.target,
            expires: form.expires || null,
          },
          topic: `Suppress: ${check.label} — ${form.target}`,
          content: form.reason.trim(),
          existingId:
            suppressEntryId[`${check.source.checkKey}::${form.target}`],
        });
      } else {
        if (!form.reason || !form.reason.trim()) {
          setError("A reason is required for an override.");
          setSaving(false);
          return;
        }
        await writeDirective({
          directive: {
            kind: "override",
            check_key: check.source.checkKey,
            value: form.value,
            expires: form.expires || null,
          },
          topic: `Override: ${check.label}`,
          content: form.reason.trim(),
          existingId: overrideByKey[check.source.checkKey]?.id,
        });
      }
      setDialog(null);
      setBump((b) => b + 1);
    } catch (ex) {
      setError(
        ex?.response?.data?.error?.message || ex?.toString?.() || "Save failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const tierLabel = tier === "None" ? "No service plan" : tier;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Tier: <strong>{tierLabel}</strong> · checks read from automated audits
          + manual entries
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Record which add-ons this customer has purchased">
          <span>
            <Button
              size="small"
              startIcon={<RedeemRoundedIcon />}
              onClick={openAddons}
              disabled={loading}
            >
              Add-ons
            </Button>
          </span>
        </Tooltip>
        <TextField
          type="date"
          size="small"
          label="Date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: todayStr }}
          sx={{ minWidth: 150 }}
        />
      </Stack>

      {audit.error && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          Automated results could not load — {audit.error}. Manual entries still
          available.
        </Alert>
      )}

      {/* A directive naming a check this page doesn't know about can never do
          anything — it is skipped silently by both _directives.py and here. Say so,
          rather than leaving a vCIO to believe a suppression or override is live. */}
      {!profile.loading && unknownDirectives.length > 0 && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          {unknownDirectives.length === 1
            ? "1 stored directive references a check this page doesn't know about, so it has no effect:"
            : `${unknownDirectives.length} stored directives reference checks this page doesn't know about, so they have no effect:`}
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {unknownDirectives.slice(0, 5).map((d) => (
              <li key={d.id}>
                {d.topic || d.kind} ({d.kind}: <code>{d.check_key || "no check_key"}</code>)
              </li>
            ))}
            {unknownDirectives.length > 5 && (
              <li>and {unknownDirectives.length - 5} more</li>
            )}
          </Box>
          Remove them from the Knowledge tab, or re-record them on the check itself.
        </Alert>
      )}

      {requeueMsg && (
        <Alert
          severity={requeueMsg.severity}
          variant="outlined"
          sx={{ mb: 2 }}
          onClose={() => setRequeueMsg(null)}
        >
          {requeueMsg.text}
        </Alert>
      )}

      {DOMAINS.map((domain) => {
        const checks = CHECKS.filter((c) => c.domain === domain.key);
        const domainEvals = checks.map((c) => evals[c.key]).filter(Boolean);
        const sum = summarizeDomain(domainEvals);
        return (
          <Accordion
            key={domain.key}
            defaultExpanded={domain.key === "infrastructure"}
            disableGutters
            sx={{
              "&:before": { display: "none" },
              mb: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
              borderRadius: 2,
              overflow: "hidden",
              // MUI rounds only the first/last accordion's outer corners by default,
              // which squares the last domain's (Obsolescence) bottom corners. Pin the
              // radius on first/last so every domain card is uniformly rounded.
              "&:first-of-type": { borderRadius: 2 },
              "&:last-of-type": { borderRadius: 2 },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ width: "100%", pr: 1 }}
              >
                <Typography sx={{ fontWeight: 700, flex: 1 }}>
                  {domain.label}
                </Typography>
                {sum.failing > 0 ? (
                  <Chip
                    size="small"
                    color="error"
                    variant="outlined"
                    label={`${sum.failing} failing`}
                  />
                ) : (
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    label="0 failing"
                  />
                )}
                <Chip
                  size="small"
                  variant="outlined"
                  label={sum.pct == null ? "No data" : `${sum.pct}%`}
                  color={
                    sum.pct == null
                      ? "default"
                      : sum.pct >= 90
                        ? "success"
                        : sum.pct >= 70
                          ? "warning"
                          : "error"
                  }
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {domain.key === "compliance" && (
                <>
                  <ComplianceSection
                    code={code}
                    checks={CHECKS}
                    evals={evals}
                    profile={compliance.data}
                    jApi={jApi}
                    onSaved={() => setBump((b) => b + 1)}
                    onOpenCheck={(check) => setDetailCheck(check)}
                  />
                  <Divider />
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ px: 2, pt: 1, display: "block" }}
                  >
                    Governance checks
                  </Typography>
                </>
              )}
              {(() => {
                // Group checks by subcategory (e.g. the DR 4×5 backup-type matrix:
                // Image / M365 / Desktop / Azure), preserving first-appearance
                // order. A blank subcategory renders ungrouped at the top.
                const groups = groupBySubcategory(checks);
                return groups.map((g, gi) => (
                  <Box key={g.sub || `_g${gi}`}>
                    {g.sub && (
                      <Box
                        sx={{
                          px: 2,
                          py: 0.75,
                          bgcolor: alpha(theme.palette.text.primary, 0.03),
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: "text.secondary",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          {g.sub}
                        </Typography>
                      </Box>
                    )}
                    {g.items.map((check, ii) => {
                      const e = evals[check.key];
                      const isManual = check.source.type === "manual";
                      const showDivider =
                        (gi > 0 || ii > 0) && !(g.sub && ii === 0);
                      return (
                        <Box key={check.key}>
                          {showDivider && <Divider />}
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{ px: 2, py: 1.25 }}
                          >
                            <StatusIcon status={e.status} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                flexWrap="wrap"
                                useFlexGap
                              >
                                <Tooltip title="View raw check data">
                                  <Typography
                                    variant="body2"
                                    onClick={() => setDetailCheck(check)}
                                    sx={{
                                      fontWeight: 600,
                                      cursor: "pointer",
                                      "&:hover": {
                                        textDecoration: "underline",
                                      },
                                    }}
                                  >
                                    {check.label}
                                  </Typography>
                                </Tooltip>
                                {e.overridden && (
                                  <Tooltip
                                    title={`Overridden by ${e.override?.recorded_by || "vCIO"}: ${e.override?.reason || ""}`}
                                  >
                                    <Chip
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                      label="override"
                                      sx={{ height: 18 }}
                                    />
                                  </Tooltip>
                                )}
                                {e.gap && (
                                  <Chip
                                    size="small"
                                    color={e.gap.color}
                                    variant="outlined"
                                    label={e.gap.label}
                                    sx={{ height: 18 }}
                                  />
                                )}
                                {e.suppressed > 0 && (
                                  <Tooltip title="One or more targets suppressed via a Knowledge directive">
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={`${e.suppressed} suppressed`}
                                      sx={{ height: 18 }}
                                    />
                                  </Tooltip>
                                )}
                              </Stack>
                              {(e.value || e.message) && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {[e.value, e.message]
                                    .filter(Boolean)
                                    .join(" — ")}
                                  {/* This check has no per-location answer because the
                                      location shares its parent's M365 tenant. The message
                                      says where the answer lives; this makes it reachable.
                                      Only rendered when the producer set tenantWide, so a
                                      NOTAPPLICABLE meaning "nothing to evaluate" does not
                                      get a link to a page that has no answer either. */}
                                  {e.tenantWide && (e.parentAccount || parentCode) && (
                                    <>
                                      {" — "}
                                      <Typography
                                        component={RouterLink}
                                        to={`/customer/${e.parentAccount || parentCode}?tab=health`}
                                        variant="caption"
                                        sx={{ color: "primary.main", fontWeight: 600 }}
                                      >
                                        view on {e.parentAccount || parentCode}
                                      </Typography>
                                    </>
                                  )}
                                </Typography>
                              )}
                              {e.status === "nodata" && !e.dataAction && !e.message && (
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                >
                                  Audit pending — {check.source.auditRunId}{" "}
                                  hasn’t run for this customer yet
                                </Typography>
                              )}
                              {e.status === "manual" && (
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                >
                                  Not yet recorded
                                </Typography>
                              )}
                              {e.status === "pending" && (
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                >
                                  {e.note ||
                                    "Automated producer for this check isn’t live yet"}
                                </Typography>
                              )}
                              {e.status === "excluded" && (
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                >
                                  Not included in the {tierLabel} plan
                                  {check.addon
                                    ? " — available as an add-on"
                                    : ""}
                                </Typography>
                              )}
                            </Box>
                            {isManual && (
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => openManual(check)}
                              >
                                Update
                              </Button>
                            )}
                            {!isManual && (
                              // An out-of-plan check can't be overridden — the plan
                              // outranks the override (see evaluateCheck). Disable
                              // rather than hide, so the reason is visible instead of
                              // the save silently doing nothing.
                              <Tooltip
                                title={
                                  e.status === "excluded"
                                    ? `Not included in the ${tierLabel} plan — an override can't grant a service the customer doesn't have`
                                    : "vCIO override"
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={e.status === "excluded"}
                                    onClick={() => openOverride(check)}
                                  >
                                    <EditRoundedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Stack>
                        </Box>
                      );
                    })}
                  </Box>
                ));
              })()}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Manual / override dialog */}
      <Dialog open={!!dialog} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog?.mode === "circuit_capacity"
            ? "Set circuit capacity"
            : form.action === "override"
              ? "Update Check"
              : "vCIO override"}
          <Typography variant="body2" color="text.secondary">
            {dialog?.check?.label}
            {dialog?.mode === "circuit_capacity" && form.target ? `-- ${form.target}` : ""}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {dialog?.mode === "manual" &&
              dialog.check.valueKind === "boolean" && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!form.bool}
                      onChange={(e) =>
                        setForm({ ...form, bool: e.target.checked })
                      }
                    />
                  }
                  label={form.bool ? "Yes" : "No"}
                />
              )}
            {dialog?.mode === "manual" && dialog.check.valueKind === "date" && (
              <TextField
                label="Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.date || ""}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            )}
            {dialog?.mode === "manual" && dialog.check.valueKind === "text" && (
              <TextField
                label="Value"
                value={form.text || ""}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
              />
            )}
            {dialog?.mode === "manual" && (
              <TextField
                label="Notes"
                multiline
                rows={2}
                value={form.notes || ""}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            )}

            {dialog?.mode === "override" && (
              <>
                {dialog.targets?.length > 0 && (
                  <FormControl size="small">
                    <InputLabel>Action</InputLabel>
                    <Select
                      label="Action"
                      value={form.action || "override"}
                      onChange={(e) =>
                        setForm({ ...form, action: e.target.value })
                      }
                    >
                      <MenuItem value="override">
                        Override the check status
                      </MenuItem>
                      <MenuItem value="suppress">
                        Suppress a specific target
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}

                {form.action === "suppress" ? (
                  <FormControl size="small">
                    <InputLabel>Target to suppress</InputLabel>
                    <Select
                      label="Target to suppress"
                      value={form.target || ""}
                      onChange={(e) =>
                        setForm({ ...form, target: e.target.value })
                      }
                    >
                      {(dialog.targets || []).map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <FormControl size="small">
                    <InputLabel>Override value</InputLabel>
                    <Select
                      label="Override value"
                      value={form.value || "PASS"}
                      onChange={(e) =>
                        setForm({ ...form, value: e.target.value })
                      }
                    >
                      <MenuItem value="PASS">Pass</MenuItem>
                      <MenuItem value="WARNING">Warning</MenuItem>
                      <MenuItem value="FAIL">Fail</MenuItem>
                    </Select>
                  </FormControl>
                )}

                <TextField
                  label="Reason"
                  required
                  multiline
                  rows={2}
                  value={form.reason || ""}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  helperText={
                    form.action === "suppress"
                      ? "Why suppress this target? (e.g. decommissioned — retained in NSM)"
                      : "Why is this being overridden?"
                  }
                />
                <TextField
                  label="Expires"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={form.expires || ""}
                  onChange={(e) =>
                    setForm({ ...form, expires: e.target.value })
                  }
                  helperText="Reverts to the automated result on this date (optional)"
                />

                {suppressByCheck[dialog.check.source.checkKey]?.size > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Current suppressions
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      {[...suppressByCheck[dialog.check.source.checkKey]].map(
                        (t) => (
                          <Stack
                            key={t}
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Chip
                              size="small"
                              variant="outlined"
                              label={t}
                              sx={{ height: 20 }}
                            />
                            <Button
                              size="small"
                              color="error"
                              onClick={() => removeSuppress(dialog.check, t)}
                              disabled={saving}
                            >
                              Remove
                            </Button>
                          </Stack>
                        ),
                      )}
                    </Stack>
                  </Box>
                )}
              </>
            )}
            {dialog?.mode === "circuit_capacity" && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Contracted circuit speed for <strong>{form.target}</strong>. Utilization is scored against these on the next producer run.
                </Typography>
                <TextField label="Download (Mbps)" type="number" required inputProps={{min: 1}}
                  value={form.downloadMbps ?? ""} onChange={(e) => setForm({...form, downloadMbps: e.target.value})}/>
                <TextField label="Upload (Mbps)" type = "number" required inputProps = {{min: 1}}
                  value = {form.uploadMbps ?? ""} onChange={(e) => setForm({...form, uploadMbps: e.target.value})}/>
                <TextField label="Provider (optional)"
                  value = {form.provider ?? ""} onChange={(e) => setForm({...form, provider: e.target.value})} />
                <TextField label="Link type (optional)"
                  value = {form.linkType ?? ""} onChange={(e) => setForm({...form, linkType: e.target.value})} />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {dialog?.existing && (
            <Button
              color="error"
              onClick={clearValue}
              disabled={saving}
              sx={{ mr: "auto" }}
            >
            {dialog.mode === "override" 
              ? "Remove override"
              : dialog.mode === "circuit_capacity"
                ? "Remove capacity"
                : "Clear value"}
            </Button>
          )}
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Raw check-data drawer */}
      <CheckDetailDrawer
        onRequeue={() => handleRequeue(detailCheck)}
        requeueBusy={requeueBusy}
        canRequeue={requeueJobIdsFor(detailCheck).length > 0}
        open={!!detailCheck}
        onClose={() => setDetailCheck(null)}
        check={detailCheck}
        evaluation={detailCheck ? evals[detailCheck.key] : null}
        records={
          detailCheck && detailCheck.source.type === "audit"
            ? auditByRunId[detailCheck.source.auditRunId] || []
            : []
        }
        manualRecord={detailCheck ? manualByKey[detailCheck.key] : null}
        override={
          detailCheck ? overrideByKey[detailCheck.source.checkKey] : null
        }
        suppressTargets={
          detailCheck
            ? suppressByCheck[detailCheck.source.checkKey] || null
            : null
        }
        onSuppressTarget={(target) =>
          openSuppressForTarget(detailCheck, target)
        }
        onUnsuppressTarget={(target) => removeSuppress(detailCheck, target)}
        circuitCapacityTargets = {
          detailCheck
            ? circuitCapacityByCheck[detailCheck.source.checkKey] || null
            : null
        }
        onSetCircuitCapacity = {(target) =>
          openCircuitCapacity(detailCheck, target)
        }
        onClearCircuitCapacity = {(target) =>
          removeCircuitCapacity(detailCheck, target)
        }
      />

      {/* Add-on entitlements */}
      <Dialog
        open={addonOpen}
        onClose={() => !addonSaving && setAddonOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add-ons purchased
          <Typography variant="body2" color="text.secondary">
            Capabilities outside the {tierLabel} plan that this customer has
            bought. Checked add-ons are evaluated as in-plan; unchecked ones
            show as excluded.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ pt: 1 }}>
            {addonError && <Alert severity="error">{addonError}</Alert>}
            {ADDONS.map((a) => (
              <FormControlLabel
                key={a.key}
                control={
                  <Switch
                    checked={!!addonForm[a.key]}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const next = { ...addonForm, [a.key]: checked };
                      // Bundled add-ons are sold as one package — toggle the whole bundle together.
                      if (a.bundle)
                        for (const b of ADDONS)
                          if (b.bundle === a.bundle) next[b.key] = checked;
                      setAddonForm(next);
                    }}
                  />
                }
                label={
                  a.bundle ? (
                    <Stack spacing={0}>
                      <span>{a.label}</span>
                      <Typography variant="caption" color="text.secondary">
                        Bundled add-on
                      </Typography>
                    </Stack>
                  ) : (
                    a.label
                  )
                }
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddonOpen(false)} disabled={addonSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveAddons}
            disabled={addonSaving}
            startIcon={addonSaving ? <CircularProgress size={16} /> : null}
          >
            {addonSaving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
